import React, { useState, useEffect } from 'react';
import { getUser } from '../utils/auth';
import { apiRequest } from '../utils/api';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Alert,
  AlertTitle,
  LinearProgress,
  Button,
  useTheme,
  alpha
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  Upload as UploadIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [estadisticas, setEstadisticas] = useState({});
  const [perfil, setPerfil] = useState({});
  const [alertas, setAlertas] = useState([]);
  const [error, setError] = useState(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const statsData = await apiRequest('/api/dashboard/estadisticas');
      const perfilData = await apiRequest('/api/dashboard/perfil');
      const alertasData = await apiRequest('/api/dashboard/alertas');

      setEstadisticas(statsData);
      setPerfil(perfilData);
      setAlertas(alertasData);
      setError(null);
    } catch (err) {
      console.error('Error al cargar dashboard:', err);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const getSeverityForAlert = (tipo) => {
    switch (tipo) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'info';
    }
  };

  const getRoleDisplayName = (rol) => {
    switch (rol) {
      case 'superadmin': return 'Super Administrador';
      case 'admin_rrhh': return 'Admin RRHH';
      case 'empleado': return 'Empleado';
      default: return rol;
    }
  };

  // Componente para estadísticas sobrio
  const StatCard = ({ icon, value, label, color }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="text.primary" sx={{ mb: 0.5 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 48,
              height: 48
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Cargando Dashboard...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchDashboardData}>
            <RefreshIcon sx={{ mr: 1 }} />
            Reintentar
          </Button>
        }>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Header */}
      <Paper sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 3,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <Box>
          <Typography variant="h5" fontWeight="600" color="text.primary" sx={{ mb: 0.5 }}>
            Bienvenido, {user?.nombre || 'Usuario'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getRoleDisplayName(user?.rol)} - {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <IconButton
          onClick={fetchDashboardData}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Paper>

      <Grid container spacing={3}>
        {/* Estadísticas Principales - Fila completa */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<PeopleIcon sx={{ fontSize: 24 }} />}
                value={estadisticas.basicas?.usuarios_activos || 0}
                label="Usuarios Activos"
                color="#5c6bc0"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<BusinessIcon sx={{ fontSize: 24 }} />}
                value={estadisticas.basicas?.empresas_activas || 0}
                label="Empresas Activas"
                color="#ec407a"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<ReceiptIcon sx={{ fontSize: 24 }} />}
                value={estadisticas.basicas?.periodos_disponibles || 0}
                label="Períodos de Recibos"
                color="#29b6f6"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<TimelineIcon sx={{ fontSize: 24 }} />}
                value={estadisticas.basicas?.total_legajos || 0}
                label="Total Legajos"
                color="#26a69a"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Estadísticas para empleados */}
        {user?.rol === 'empleado' && estadisticas.empleado && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  textAlign: 'center',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}>
                  <AssignmentIcon color="primary" sx={{ fontSize: 28 }} />
                  <Typography variant="h5" fontWeight="600" sx={{ mt: 1 }}>
                    {estadisticas.empleado.total_recibos || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total de Recibos
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  textAlign: 'center',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
                }}>
                  <AssignmentTurnedInIcon color="success" sx={{ fontSize: 28 }} />
                  <Typography variant="h5" fontWeight="600" sx={{ mt: 1 }}>
                    {estadisticas.empleado.recibos_firmados || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Recibos Firmados
                  </Typography>
                </Box>
              </Grid>
              {estadisticas.empleado.ultimo_periodo && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Último Período
                    </Typography>
                    <Typography variant="h5" fontWeight="600">
                      {estadisticas.empleado.ultimo_periodo}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<ReceiptIcon />}
                  onClick={() => navigate('/recibos')}
                  sx={{ height: '100%', minHeight: 80 }}
                >
                  Ver Mis Recibos
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Estadísticas para administradores */}
        {(user?.rol === 'admin_rrhh' || user?.rol === 'superadmin') && estadisticas.admin && (
          <>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    textAlign: 'center',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                  }}>
                    <UploadIcon color="primary" sx={{ fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="600" sx={{ mt: 1 }}>
                      {estadisticas.admin.importaciones_hoy || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Importaciones Hoy
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    textAlign: 'center',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                  }}>
                    <ReceiptIcon color="info" sx={{ fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="600" sx={{ mt: 1 }}>
                      {estadisticas.admin.recibos_semana || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Recibos Esta Semana
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    textAlign: 'center',
                    border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
                  }}>
                    <PersonAddIcon color="success" sx={{ fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="600" sx={{ mt: 1 }}>
                      {estadisticas.admin.usuarios_nuevos_mes || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Usuarios Nuevos
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    textAlign: 'center',
                    border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`
                  }}>
                    <ErrorIcon color="error" sx={{ fontSize: 28 }} />
                    <Typography variant="h5" fontWeight="600" sx={{ mt: 1 }}>
                      {estadisticas.admin.errores_semana || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Errores Esta Semana
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Rendimiento del Sistema */}
            <Grid item xs={12}>
              <Card sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                height: '100%'
              }}>
                <Box sx={{
                  p: 2,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <TrendingUpIcon color="success" />
                  <Typography variant="h6" fontWeight="600">
                    Rendimiento del Sistema
                  </Typography>
                </Box>
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="500">Recibos Firmados</Typography>
                      <Chip
                        label={`${estadisticas.admin?.porcentaje_firmados || 0}%`}
                        size="small"
                        color="primary"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={estadisticas.admin?.porcentaje_firmados || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.primary.main, 0.1)
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="500">Usuarios Activos</Typography>
                      <Chip
                        label={`${estadisticas.admin?.porcentaje_usuarios_activos || 0}%`}
                        size="small"
                        color="secondary"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={estadisticas.admin?.porcentaje_usuarios_activos || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.secondary.main, 0.1)
                      }}
                      color="secondary"
                    />
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" fontWeight="500">Legajos Completos</Typography>
                      <Chip
                        label={`${estadisticas.admin?.porcentaje_legajos_completos || 0}%`}
                        size="small"
                        color="success"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={estadisticas.admin?.porcentaje_legajos_completos || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.success.main, 0.1)
                      }}
                      color="success"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Alertas */}
        {alertas.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Box sx={{
                p: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <ErrorIcon color="warning" />
                <Typography variant="h6" fontWeight="600">
                  Alertas y Notificaciones
                </Typography>
              </Box>
              <CardContent>
                <Grid container spacing={2}>
                  {alertas.map((alerta, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Alert
                        severity={getSeverityForAlert(alerta.tipo)}
                        action={
                          alerta.accion && (
                            <Button
                              color="inherit"
                              size="small"
                              onClick={() => navigate(alerta.accion)}
                            >
                              Ver
                            </Button>
                          )
                        }
                        sx={{ borderRadius: 1 }}
                      >
                        <AlertTitle>{alerta.titulo}</AlertTitle>
                        {alerta.descripcion}
                      </Alert>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
